/**
 * Busca Preditiva - Funcionalidade de busca em tempo real
 */
class PredictiveSearch {
  constructor() {
    this.searchInput = document.querySelector('.main-search__input');
    this.predictiveSearch = document.getElementById('predictive-search');
    this.searchTimeout = null;
    this.isSearching = false;
    this.currentQuery = '';
    this.abortController = null; // Para cancelar requests
    
    if (!this.searchInput || !this.predictiveSearch) {
      return;
    }
    this.init();
  }
  
  init() {
    this.bindEvents();
    this.setupClickOutside();
    this.setupResizeListener();
    this.setupBeforeUnloadListener();
    this.setupMobileSearchToggle();
  }
  
  bindEvents() {
    // Input de busca
    this.searchInput.addEventListener('input', (e) => {
      this.handleSearchInput(e.target.value);
    });
    
    this.searchInput.addEventListener('focus', () => {
      // Removido: busca automática no focus
      // Agora só abre quando o usuário digitar
    });
    
    this.searchInput.addEventListener('keydown', (e) => {
      if (e.key === 'Escape') {
        this.hidePredictiveSearch();
        this.searchInput.blur();
      }
    });
    
    // Botão de fechar
    const closeBtn = this.predictiveSearch.querySelector('[data-action="close-predictive-search"]');
    if (closeBtn) {
      closeBtn.addEventListener('click', (e) => {
        e.preventDefault();
        e.stopPropagation();
        this.hidePredictiveSearch();
        // Não focar automaticamente para evitar reabertura
      });
    }
    
    // Form de busca
    const searchForm = this.searchInput.closest('form');
    if (searchForm) {
      searchForm.addEventListener('submit', () => {
        this.hidePredictiveSearch();
      });
    }
  }
  
  setupClickOutside() {
    document.addEventListener('click', (e) => {
      // Verificar se o clique foi fora da área de busca E fora da busca preditiva
      const isClickInsideSearchArea = this.searchInput === e.target || this.searchInput.contains(e.target);
      const isClickInsidePredictiveSearch = this.predictiveSearch.contains(e.target);
      
      if (!isClickInsideSearchArea && !isClickInsidePredictiveSearch) {
        this.hidePredictiveSearch();
      }
    });
  }
  
  setupResizeListener() {
    // Libera o scroll quando a tela for redimensionada para desktop
    window.addEventListener('resize', () => {
      if (window.innerWidth > 768) {
        this.releaseScroll();
      }
    });
  }
  
  releaseScroll() {
    // Função para liberar o scroll completamente
    document.body.classList.remove('predictive-search-active');
    document.body.style.overflow = '';
    document.body.style.position = '';
    document.body.style.top = '';
    document.body.style.left = '';
    document.body.style.right = '';
    document.body.style.bottom = '';
    document.body.style.width = '';
  }
  
  setupBeforeUnloadListener() {
    // Garante que o scroll seja liberado quando a página for fechada/recarregada
    window.addEventListener('beforeunload', () => {
      this.releaseScroll();
    });
  }
  
  setupMobileSearchToggle() {
    const mobileSearchToggle = document.querySelector('[data-action="toggle-search-mobile"]');
    const searchWrapper = document.querySelector('.header-search-wrapper');
    
    if (mobileSearchToggle && searchWrapper) {
      mobileSearchToggle.addEventListener('click', () => {
        searchWrapper.classList.toggle('search-active');
        
        if (searchWrapper.classList.contains('search-active')) {
          // Focar no input quando abrir
          setTimeout(() => {
            this.searchInput.focus();
          }, 100);
        }
      });
    }
  }
  
  handleSearchInput(query) {
    const trimmedQuery = query.trim();
    
    // Log do input (apenas para debug)
    if (trimmedQuery.length > 0 && window.location.hostname === 'localhost') {
      console.log(`⌨️ Usuário digitou: "${trimmedQuery}" (${trimmedQuery.length} caracteres)`);
    }
    
    // Limpar timeout anterior
    if (this.searchTimeout) {
      clearTimeout(this.searchTimeout);
    }
    
    // Esconder busca preditiva se não há query
    if (trimmedQuery.length === 0) {
      console.log('🚫 Campo vazio - escondendo busca');
      this.hidePredictiveSearch();
      return;
    }
    
    // Mostrar busca preditiva
    this.showPredictiveSearch();
    
    // Debounce de 500ms para evitar muitas requisições
    this.searchTimeout = setTimeout(() => {
      console.log(`🔄 Executando busca para: "${trimmedQuery}"`);
      this.performSearch(trimmedQuery);
    }, 500);
  }
  
  async performSearch(query) {
    // Cancelar request anterior se existir
    if (this.abortController) {
      this.abortController.abort();
    }
    
    // Evitar múltiplas buscas simultâneas
    if (this.isSearching) {
      return;
    }
    
    this.isSearching = true;
    this.currentQuery = query; // Armazenar para uso no highlight
    this.abortController = new AbortController();
    this.showLoading();
    
    try {
      // Primeira tentativa: busca geral (6 de cada)
      let results = await this.fetchSearchResults(query, this.abortController.signal);
      
      // Se encontrou produtos, buscar mais produtos para o swiper
      if (results.resources?.results?.products?.length > 0) {
        console.log('Buscando mais produtos para o swiper...');
        const moreProducts = await this.fetchMoreProducts(query, this.abortController.signal);
        if (moreProducts && moreProducts.resources?.results?.products) {
          // Mesclar produtos únicos
          const existingIds = new Set(results.resources.results.products.map(p => p.id));
          const newProducts = moreProducts.resources.results.products.filter(p => !existingIds.has(p.id));
          results.resources.results.products = [...results.resources.results.products, ...newProducts];
        }
      }
      
      // Se não encontrou resultados suficientes, tenta busca mais específica
      if (!this.hasAnyResults(results) && query.length >= 2) {
        console.log('Tentando busca fallback...');
        results = await this.fetchFallbackResults(query, this.abortController.signal);
      }
      
      this.displayResults(results, query);
    } catch (error) {
      // Não mostrar erro se foi cancelado
      if (error.name !== 'AbortError') {
        console.error('Erro na busca preditiva:', error);
        this.showError();
      }
    } finally {
      this.isSearching = false;
      this.abortController = null;
    }
  }
  
  async fetchSearchResults(query, signal = null) {
    // Busca com sintaxe correta do Shopify
    const searchParams = new URLSearchParams({
      'q': query,
      'resources[type]': 'query,product,collection,page,article',
      'resources[limit]': '6',
      'resources[limit_scope]': 'each',
      // Campos para busca mais abrangente
      'resources[options][fields]': 'title,body,tag,author,product_type,vendor'
    });
    
    const searchUrl = `/search/suggest.json?${searchParams.toString()}`;
    
    const response = await fetch(searchUrl, {
      signal: signal // Para poder cancelar o request
    });
    if (!response.ok) {
      throw new Error('Erro na requisição');
    }
    
    const results = await response.json();
    
    // Debug: verificar todos os tipos de resultados
    console.log('🔍 Predictive Search Results (Expandida):', {
      query: query,
      searchUrl: searchUrl,
      queries: results.resources?.results?.queries || [],
      products: results.resources?.results?.products?.length || 0,
      collections: results.resources?.results?.collections?.length || 0,
      pages: results.resources?.results?.pages?.length || 0,
      articles: results.resources?.results?.articles?.length || 0
    });
    
    // Log detalhado dos termos encontrados (apenas em desenvolvimento)
    if (window.location.hostname === 'localhost' || window.location.hostname.includes('127.0.0.1')) {
      this.logTermsFound(query, results);
    }
    
    return results;
  }
  
  async fetchMoreProducts(query, signal = null) {
    // Busca focada apenas em produtos para obter mais resultados
    const searchParams = new URLSearchParams({
      'q': query,
      'resources[type]': 'product',
      'resources[limit]': '10',
      'resources[limit_scope]': 'each',
      'resources[options][fields]': 'title,product_type,vendor,metafields.custom.composicao,metafields.custom.cor,metafields.custom.elasticidade,metafields.custom.tecido_largura,metafields.custom.padronagem,metafields.custom.tecido_gramatura'
    });
    
    const searchUrl = `/search/suggest.json?${searchParams.toString()}`;
    
    try {
      const response = await fetch(searchUrl, {
        signal: signal
      });
      if (!response.ok) {
        return null;
      }
      
      const results = await response.json();
      console.log('🛍️ Produtos adicionais encontrados:', results.resources?.results?.products?.length || 0);
      return results;
    } catch (error) {
      console.log('Erro ao buscar produtos adicionais:', error.message);
      return null;
    }
  }
  
  async fetchFallbackResults(query, signal = null) {
    // Busca fallback simplificada
    try {
      const searchParams = new URLSearchParams({
        'q': query,
        'resources[type]': 'product,collection,article',
        'resources[limit]': '6',
        'resources[limit_scope]': 'each',
        'resources[options][fields]': 'title'
      });
      
      const searchUrl = `/search/suggest.json?${searchParams.toString()}`;
      const response = await fetch(searchUrl, {
        signal: signal // Para poder cancelar o request
      });
        
      if (response.ok) {
        const results = await response.json();
        
        console.log('Fallback strategy results:', {
          products: results.resources?.results?.products?.length || 0,
          collections: results.resources?.results?.collections?.length || 0,
          articles: results.resources?.results?.articles?.length || 0
        });
        
        if (this.hasAnyResults(results)) {
          return results;
        }
      }
    } catch (error) {
      console.warn('Fallback strategy failed:', error);
    }
    
    // Se a estratégia falhou, retorna resultado vazio
    return {
      resources: {
        results: {
          queries: [],
          products: [],
          collections: [],
          pages: [],
          articles: []
        }
      }
    };
  }
  
  displayResults(results, query) {
    this.hideAllStates();
    
    const hasResults = this.hasAnyResults(results);
    
    if (!hasResults) {
      this.showNoResults(query);
      return;
    }
    
    this.showResults(results);
    this.updateViewAllLink(query);
  }
  
  hasAnyResults(results) {
    return (
      (results.resources?.results?.queries?.length > 0) ||
      (results.resources?.results?.products?.length > 0) ||
      (results.resources?.results?.collections?.length > 0) ||
      (results.resources?.results?.pages?.length > 0) ||
      (results.resources?.results?.articles?.length > 0)
    );
  }
  
  showResults(results) {
    const resultsContainer = this.predictiveSearch.querySelector('.predictive-search-results');
    const queriesContainer = this.predictiveSearch.querySelector('.predictive-search-queries');
    const productsContainer = this.predictiveSearch.querySelector('.predictive-search-products');
    const collectionsContainer = this.predictiveSearch.querySelector('.predictive-search-collections');
    const articlesContainer = this.predictiveSearch.querySelector('.predictive-search-articles');
    const pagesContainer = this.predictiveSearch.querySelector('.predictive-search-pages');
    
    // Limpar containers
    queriesContainer.innerHTML = '';
    productsContainer.innerHTML = '';
    collectionsContainer.innerHTML = '';
    articlesContainer.innerHTML = '';
    pagesContainer.innerHTML = '';
    
    // Termos mais buscados (queries)
    if (results.resources?.results?.queries?.length > 0) {
      this.renderQueries(results.resources.results.queries, queriesContainer);
    }
    
    // Produtos (com grid)
    if (results.resources?.results?.products?.length > 0) {
      this.renderProducts(results.resources.results.products, productsContainer);
    }
    
    // Coleções (máximo 3)
    if (results.resources?.results?.collections?.length > 0) {
      this.renderCollections(results.resources.results.collections.slice(0, 3), collectionsContainer);
    }
    
    // Posts do blog (articles) (buscar 10, filtrar e mostrar 3)
    if (results.resources?.results?.articles?.length > 0) {
      this.renderArticles(results.resources.results.articles.slice(0, 10), articlesContainer);
    }
    
    // Páginas (buscar 10, filtrar e mostrar 3)
    if (results.resources?.results?.pages?.length > 0) {
      this.renderPages(results.resources.results.pages.slice(0, 10), pagesContainer);
    }
    
    resultsContainer.style.display = 'flex';
    
    // Verificar se sidebar tem conteúdo
    this.checkSidebarContent(resultsContainer);
  }
  
  checkSidebarContent(resultsContainer) {
    const sidebar = resultsContainer.querySelector('.predictive-search-sidebar');
    const sidebarSections = sidebar.querySelectorAll('.predictive-search-section');
    
    // Verificar se alguma seção da sidebar tem conteúdo
    let hasContent = false;
    sidebarSections.forEach(section => {
      const items = section.querySelectorAll('.predictive-search-item, .predictive-search-query, .predictive-search-article, .predictive-search-collection, .predictive-search-page');
      if (items.length > 0) {
        hasContent = true;
      }
    });
    
    // Adicionar ou remover classe baseado no conteúdo
    if (hasContent) {
      resultsContainer.classList.remove('sidebar-empty');
    } else {
      resultsContainer.classList.add('sidebar-empty');
    }
  }
  
  renderQueries(queries, container) {
    const queriesHTML = queries.map(query => `
      <div class="predictive-search-item predictive-search-query link-holder">
        <a href="/search?q=${encodeURIComponent(query.text)}" class="predictive-search-item-link link-holder">
          <h4 class="predictive-search-item-title link-default">${query.text}</h4>
        </a>
      </div>
    `).join('');
    
    container.innerHTML = `
      <div class="predictive-search-section">
        <h4 class="predictive-search-section-title">Termos mais buscados</h4>
        <div class="predictive-search-items">
          ${queriesHTML}
        </div>
      </div>
    `;
  }

  renderProducts(products, container) {
    console.log('🎨 Renderizando produtos:', products.length);
    
    const productsHTML = products.map(product => {
      console.log('🛍️ Processando produto:', product.title);
      return `
      <div class="swiper-slide">
        <div class="predictive-search-product-card">
          <a href="${product.url}" class="predictive-search-product-link link-holder">
            <div class="predictive-search-product-image">
              ${product.featured_image?.url || product.image ? `
                <img src="${product.featured_image?.url || product.image}" 
                     alt="${product.featured_image?.alt || product.title}" 
                     loading="lazy">
              ` : ''}
            </div>
                          <div class="predictive-search-product-info">
                <h4 class="predictive-search-product-title link-default">${product.title}</h4>
                <div class="predictive-search-product-prices">
                  ${this.renderProductPrice(product)}
                </div>
              </div>
          </a>
        </div>
      </div>
      `;
    }).join('');
    
    container.innerHTML = `
      <div class="predictive-search-section">
        <h4 class="predictive-search-section-title">Produtos</h4>
        <div class="predictive-search-products-swiper">
          <div class="swiper">
            <div class="swiper-wrapper">
              ${productsHTML}
            </div>
            <div class="swiper-pagination"></div>
            <div class="swiper-button-prev"></div>
            <div class="swiper-button-next"></div>
          </div>
          <a href="#" class="link-default" style="display: none;" data-view-all-link>
            <span>Ver todos os resultados</span>
          </a>
        </div>
      </div>
    `;
    
    // Inicializar Swiper após renderizar
    this.initProductsSwiper();
  }
  
  renderCollections(collections, container) {
    const collectionsHTML = collections.map(collection => `
      <div class="predictive-search-item predictive-search-collection link-holder">
        <a href="${collection.url}" class="predictive-search-item-link link-holder">
          <h4 class="predictive-search-item-title link-default">${collection.title}</h4>
        </a>
      </div>
    `).join('');
    
    container.innerHTML = `
      <div class="predictive-search-section">
        <h4 class="predictive-search-section-title">Categorias</h4>
        <div class="predictive-search-items">
          ${collectionsHTML}
        </div>
      </div>
    `;
  }
  
  renderArticles(articles, container) {
    // Filtrar posts que NÃO contêm "ÁGOMAS" no título
    const filteredArticles = articles.filter(article => 
      !article.title.toLowerCase().includes('ágomas') && 
      !article.title.toLowerCase().includes('agomas')
    );
    
    // Pegar apenas os 3 primeiros após o filtro
    const limitedArticles = filteredArticles.slice(0, 3);
    
    const articlesHTML = limitedArticles.map(article => `
      <div class="predictive-search-item predictive-search-article link-holder">
        <a href="${article.url}" class="predictive-search-item-link link-holder">
          <h4 class="predictive-search-item-title link-default">${article.title}</h4>
          ${article.tags && article.tags.length > 0 ? `<p class="predictive-search-item-subtitle">${article.tags.slice(0, 2).join(', ')}</p>` : ''}
        </a>
      </div>
    `).join('');
    
    container.innerHTML = `
      <div class="predictive-search-section">
        <h4 class="predictive-search-section-title">Blog</h4>
        <div class="predictive-search-items">
          ${articlesHTML}
        </div>
      </div>
    `;
  }

  renderPages(pages, container) {
    // Filtrar páginas que NÃO contêm "em Português" no título
    const filteredPages = pages.filter(page => 
      !page.title.toLowerCase().includes('em português') && 
      !page.title.toLowerCase().includes('em portugues')
    );
    
    // Pegar apenas as 3 primeiras após o filtro
    const limitedPages = filteredPages.slice(0, 3);
    
    const pagesHTML = limitedPages.map(page => `
      <div class="predictive-search-item predictive-search-page link-holder">
        <a href="${page.url}" class="predictive-search-item-link link-holder">
          <h4 class="predictive-search-item-title link-default">${page.title}</h4>
        </a>
      </div>
    `).join('');
    
    container.innerHTML = `
      <div class="predictive-search-section">
        <h4 class="predictive-search-section-title">Páginas</h4>
        <div class="predictive-search-items">
          ${pagesHTML}
        </div>
      </div>
    `;
  }
  
  showLoading() {
    this.hideAllStates();
    this.predictiveSearch.querySelector('.predictive-search-loading').style.display = 'flex';
  }
  
  showNoResults(query) {
    this.hideAllStates();
    const noResults = this.predictiveSearch.querySelector('.predictive-search-no-results');
    noResults.querySelector('.search-query').textContent = query;
    noResults.style.display = 'block';
  }
  
  showError() {
    this.hideAllStates();
    this.predictiveSearch.querySelector('.predictive-search-error').style.display = 'block';
  }
  
  hideAllStates() {
    const states = [
      '.predictive-search-loading',
      '.predictive-search-results',
      '.predictive-search-no-results',
      '.predictive-search-error'
    ];
    
    states.forEach(selector => {
      const element = this.predictiveSearch.querySelector(selector);
      if (element) element.style.display = 'none';
    });
  }
  
  updateViewAllLink(query) {
    const viewAllLink = this.predictiveSearch.querySelector('[data-view-all-link]');
    if (viewAllLink) {
      viewAllLink.href = `/search?q=${encodeURIComponent(query)}`;
      viewAllLink.style.display = 'flex';
      viewAllLink.style.width = 'fit-content';
      viewAllLink.style.margin = '10px auto 20px';
    }
  }
  
  showPredictiveSearch() {
    this.predictiveSearch.style.display = 'flex';
    
    // Trava o scroll no mobile
    if (window.innerWidth <= 768) {
      document.body.classList.add('predictive-search-active');
      
      // Força o travamento do scroll
      document.body.style.overflow = 'hidden';
      document.body.style.position = 'fixed';
      document.body.style.top = '0';
      document.body.style.left = '0';
      document.body.style.right = '0';
      document.body.style.bottom = '0';
      document.body.style.width = '100%';
    }
  }
  
  hidePredictiveSearch() {
    this.predictiveSearch.style.display = 'none';
    this.clearResults();
    
    // Libera o scroll no mobile
    if (window.innerWidth <= 768) {
      this.releaseScroll();
    }
  }
  
  clearResults() {
    // Limpar todos os containers de resultados
    const containers = [
      '.predictive-search-queries',
      '.predictive-search-collections', 
      '.predictive-search-articles',
      '.predictive-search-pages',
      '.predictive-search-products'
    ];
    
    containers.forEach(selector => {
      const element = this.predictiveSearch.querySelector(selector);
      if (element) element.innerHTML = '';
    });
    
    // Esconder o container principal de resultados
    const resultsContainer = this.predictiveSearch.querySelector('.predictive-search-results');
    if (resultsContainer) {
      resultsContainer.style.display = 'none';
    }
  }
  

  
  // Métodos utilitários para melhorar os resultados
  highlightText(text) {
    if (!this.currentQuery || !text) return text;
    
    const regex = new RegExp(`(${this.escapeRegex(this.currentQuery)})`, 'gi');
    return text.replace(regex, '<mark class="predictive-search-highlight">$1</mark>');
  }
  
  truncateText(text, maxLength) {
    if (!text || text.length <= maxLength) return text;
    
    // Encontra o último espaço antes do limite
    const truncated = text.substring(0, maxLength);
    const lastSpace = truncated.lastIndexOf(' ');
    
    if (lastSpace > 0) {
      return truncated.substring(0, lastSpace) + '...';
    }
    
    return truncated + '...';
  }
  
  escapeRegex(string) {
    return string.replace(/[.*+?^${}()|[\]\\]/g, '\\$&');
  }
  
  // Log detalhado dos termos encontrados
  logTermsFound(query, results) {
    console.group(`📋 Análise de Termos para: "${query}"`);
    
    // Queries/Termos sugeridos
    if (results.resources?.results?.queries?.length > 0) {
      console.log('💭 Termos sugeridos:', results.resources.results.queries.map(q => q.text));
    }
    
    // Produtos encontrados
    if (results.resources?.results?.products?.length > 0) {
      console.log('🛍️ Produtos encontrados:');
      results.resources.results.products.forEach((product, index) => {
        console.log(`  ${index + 1}. ${product.title} - ${product.vendor || 'Sem marca'} - ${product.product_type || 'Sem categoria'}`);
        if (product.tags && product.tags.length > 0) {
          console.log(`     Tags: ${product.tags.join(', ')}`);
        }
      });
    }
    
    // Coleções encontradas
    if (results.resources?.results?.collections?.length > 0) {
      console.log('📁 Coleções encontradas:');
      results.resources.results.collections.forEach((collection, index) => {
        console.log(`  ${index + 1}. ${collection.title}`);
      });
    }
    
    // Posts do blog encontrados
    if (results.resources?.results?.articles?.length > 0) {
      console.log('📝 Posts do blog encontrados:');
      results.resources.results.articles.forEach((article, index) => {
        console.log(`  ${index + 1}. ${article.title}${article.author ? ` - Por ${article.author}` : ''}`);
        if (article.tags && article.tags.length > 0) {
          console.log(`     Tags: ${article.tags.join(', ')}`);
        }
      });
    }
    
    // Páginas encontradas
    if (results.resources?.results?.pages?.length > 0) {
      console.log('📄 Páginas encontradas:');
      results.resources.results.pages.forEach((page, index) => {
        console.log(`  ${index + 1}. ${page.title}${page.author ? ` - Por ${page.author}` : ''}`);
      });
    }
    
    console.groupEnd();
  }
  
  renderProductPrice(product) {
    console.log('🏷️ Renderizando preço para produto:', product.title);
    console.log('📦 Dados completos do produto da busca:', product);
    console.log('🔗 URL do produto:', product.url);
    console.log('🧬 Variantes retornadas pela Predictive Search:', product.variants);
    
    // Extrair handle da URL, removendo parâmetros de query
    const productHandle = product.url.split('/').pop().split('?')[0];
    console.log('🎯 Handle extraído (limpo):', productHandle);
    
    // Criar um placeholder único para este produto
    const priceId = `price-${productHandle}-${Date.now()}`;
    console.log('🆔 ID do elemento preço:', priceId);
    
    // Sempre usar a API individual do produto pois a Predictive Search
    // só retorna variantes quando a busca corresponde ao título da variante
    setTimeout(() => {
      this.fetchProductPrice(productHandle, priceId);
    }, 100);
    
    return `<span id="${priceId}" class="predictive-search-regular-price"></span>`;
  }
  
  async fetchProductPrice(productHandle, priceElementId) {
    try {
      console.log('💰 Buscando preço para handle:', productHandle);
      
      // Usar a API de produtos do Shopify
      const response = await fetch(`/products/${productHandle}.js`);
      if (!response.ok) {
        console.error('❌ Resposta da API não OK:', response.status, response.statusText);
        throw new Error(`Produto não encontrado: ${response.status}`);
      }
      
      const product = await response.json();
      console.log('📦 Produto completo encontrado:', product);
      console.log('🔍 Estrutura do produto:');
      console.log('- ID:', product.id);
      console.log('- Title:', product.title);
      console.log('- Handle:', product.handle);
      console.log('- Variants length:', product.variants?.length);
      console.log('- Variants completas:', product.variants);
      
      if (product.variants && product.variants.length > 0) {
        const variant = product.variants[0]; // Primeira variante
        console.log('💎 Primeira variante:', variant);
        console.log('- Variant ID:', variant.id);
        console.log('- Variant price (raw):', variant.price);
        console.log('- Variant compare_at_price (raw):', variant.compare_at_price);
        
        const price = variant.price / 100;
        const comparePrice = variant.compare_at_price ? variant.compare_at_price / 100 : null;
        
        console.log('💵 Preços processados:', { price, comparePrice });
        
        let priceHTML = '';
        
        if (comparePrice && comparePrice > price) {
          // Produto em promoção
          priceHTML = `
            <span class="predictive-search-compare-price">R$ ${comparePrice.toFixed(2).replace('.', ',')}</span>
            <span class="predictive-search-sale-price">R$ ${price.toFixed(2).replace('.', ',')}</span>
          `;
          console.log('🔥 Produto em promoção renderizado');
        } else {
          // Preço normal
          priceHTML = `
            <span class="predictive-search-regular-price">R$ ${price.toFixed(2).replace('.', ',')}</span>
          `;
          console.log('💲 Preço normal renderizado');
        }
        
        // Atualizar o elemento na página
        const priceElement = document.getElementById(priceElementId);
        if (priceElement) {
          priceElement.outerHTML = priceHTML;
          console.log('✅ Preço atualizado no DOM para:', productHandle);
        } else {
          console.error('❌ Elemento de preço não encontrado:', priceElementId);
        }
      } else {
        console.error('❌ Produto sem variantes ou variantes vazias:', {
          hasVariants: !!product.variants,
          variantsLength: product.variants?.length,
          variants: product.variants
        });
        throw new Error('Produto sem variantes');
      }
    } catch (error) {
      console.error('❌ Erro completo ao buscar preço:', error);
      console.error('- Handle tentado:', productHandle);
      console.error('- Element ID:', priceElementId);
      
      const priceElement = document.getElementById(priceElementId);
      if (priceElement) {
        priceElement.textContent = '';
        priceElement.className = 'predictive-search-regular-price';
      }
    }
  }
  
  initProductsSwiper() {
    // Aguardar o próximo frame para garantir que o DOM foi atualizado
    requestAnimationFrame(() => {
      const swiperElement = this.predictiveSearch.querySelector('.predictive-search-products-swiper .swiper');
      if (swiperElement && window.Swiper) {
        new Swiper(swiperElement, {
          slidesPerView: 2,
          spaceBetween: 12,
          pagination: {
            el: '.swiper-pagination'
          },
          navigation: {
            nextEl: '.swiper-button-next',
            prevEl: '.swiper-button-prev',
          },
          breakpoints: {
            500: {
              slidesPerView: 3,
              spaceBetween: 16,
            },
            1200: {
              slidesPerView: 4,
              spaceBetween: 16,
            },
            1400: {
              slidesPerView: 5,
              spaceBetween: 16,
            }
          }
        });
      }
    });
  }
}

// Inicializar quando o DOM estiver pronto
document.addEventListener('DOMContentLoaded', () => {
  new PredictiveSearch();
});

